// src/components/shared/envUtils.js
// Shared utilities for the multi-environment promote workflow.
//
// All config-list APIs return data as [devArray, testArray, prodArray].
// These helpers eliminate the repeated env-slice / column-value logic
// that was copied across fileTransfer, eiConnection, apiConfig,
// serviceConfig, and taskSchedule.

/**
 * Slice a [dev, test, prod] data array into named buckets.
 *
 * @param {Array[]} data - Array of 3 arrays returned by GetDataFromAllEnv
 * @param {string}  env  - Current env string: "DEV" | "TEST" | "PROD"
 * @returns {{ current: Array, dev: Array, test: Array, prod: Array }}
 */
export function sliceByEnv(data, env) {
  const dev  = data[0] ?? [];
  const test = data[1] ?? [];
  const prod = data[2] ?? [];

  const current =
    env === "TEST" ? test :
    env === "PROD" ? prod :
    dev; // default / DEV

  return { current, dev, test, prod };
}

/**
 * Resolve the promote-column value for a single row across environments.
 *
 * @param {object} opts
 * @param {string}   opts.envColumn  - "Dev" | "Test" | "Prod"
 * @param {Array}    opts.dev        - Dev env config rows
 * @param {Array}    opts.test       - Test env config rows
 * @param {Array}    opts.prod       - Prod env config rows
 * @param {string}   [opts.idKey]    - Field name used to match rows (single key)
 * @param {*}        [opts.idValue]  - Value to match against idKey
 * @param {string}   [opts.valueKey="STATE"] - Field to read from the matched row
 * @param {Function} [opts.matcher]  - Custom predicate `(row) => boolean`
 *                                     Takes precedence over idKey/idValue when provided.
 * @returns {string} The matched field value, or "N/A" if not found.
 */
export function getEnvColumnValue({
  envColumn,
  dev,
  test,
  prod,
  idKey,
  idValue,
  valueKey = "STATE",
  matcher,
}) {
  const pool =
    envColumn === "Dev"  ? dev  :
    envColumn === "Test" ? test :
    envColumn === "Prod" ? prod :
    [];

  const found = matcher
    ? pool.find(matcher)
    : pool.find((row) => row[idKey] === idValue);

  return found ? found[valueKey] : "N/A";
}

/**
 * Resolve the full matched row (not just one field) for a target env.
 * Same matching semantics as getEnvColumnValue, used by the Diff/Match
 * feature which needs to compare and copy whole records.
 *
 * @param {object} opts - Same shape as getEnvColumnValue (minus valueKey).
 * @returns {object|null} The matched row, or null if not found.
 */
export function getEnvRow({ envColumn, dev, test, prod, idKey, idValue, matcher }) {
  const pool =
    envColumn === "Dev"  ? dev  :
    envColumn === "Test" ? test :
    envColumn === "Prod" ? prod :
    [];

  const found = matcher
    ? pool.find(matcher)
    : pool.find((row) => row[idKey] === idValue);

  return found ?? null;
}

/**
 * Fields that never count toward a Diff, regardless of entity:
 * audit columns, plus anything caller-supplied.
 */
export const DEFAULT_DIFF_EXCLUDE_FIELDS = [
  "CREATED_TS",
  "UPDATED_TS",
  "CREATED_BY",
  "UPDATED_BY",
];

/**
 * Field-by-field comparison between a row in the current env and the
 * matched row in a target env. Internal/matching keys (leading "_",
 * e.g. _API_ID, _cmdCount_) and audit columns are always ignored.
 *
 * @param {object|null} currentRow
 * @param {object|null} targetRow
 * @param {string[]} [extraExcludeFields] - Additional fields to ignore for this entity.
 * @returns {{ isDifferent: boolean, diffFields: string[] }}
 */
export function getRowDiff(currentRow, targetRow, extraExcludeFields = []) {
  if (!currentRow || !targetRow) {
    return { isDifferent: false, diffFields: [] };
  }

  const excluded = new Set([...DEFAULT_DIFF_EXCLUDE_FIELDS, ...extraExcludeFields]);
  const keys = new Set([...Object.keys(currentRow), ...Object.keys(targetRow)]);
  const diffFields = [];

  keys.forEach((key) => {
    if (key.startsWith("_") || excluded.has(key)) {
      return;
    }

    const currentValue = currentRow[key] ?? "";
    const targetValue = targetRow[key] ?? "";

    if (String(currentValue) !== String(targetValue)) {
      diffFields.push(key);
    }
  });

  return { isDifferent: diffFields.length > 0, diffFields };
}

/**
 * Build a full record for the write payload: start from the current row's
 * values, then swap in the target row's value for each field in
 * `selectedFields`. Used by the Match action so a user can apply all of a
 * record's differences, or just the ones they pick.
 *
 * @param {string[]} fieldNames - Every business field the entity's payload needs.
 * @param {object} currentRow
 * @param {object} targetRow
 * @param {string[]} selectedFields - Subset of fieldNames to pull from targetRow.
 * @returns {object}
 */
export function mergeFields(fieldNames, currentRow, targetRow, selectedFields) {
  const selected = new Set(selectedFields);

  return fieldNames.reduce((acc, field) => {
    acc[field] = selected.has(field) ? targetRow[field] : currentRow[field];
    return acc;
  }, {});
}
